import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Save } from 'lucide-react';
import { GuestEvent, guestAPI } from '../../services/guestAPI';
import { logError } from '../../utils/errorLogger';
import { formatGuestEventDisplayName } from '../../utils/guestNameUtils';

interface GuestCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  guest: GuestEvent;
  eventId: string;
  onUpdate: (updatedGuest: GuestEvent) => void;
  theme: 'dark' | 'light';
  userRole?: 'admin' | 'host';
}

export const GuestCommentModal: React.FC<GuestCommentModalProps> = ({
  isOpen,
  onClose,
  guest,
  eventId,
  onUpdate,
  theme,
  userRole = 'host'
}) => {
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);

  // Load comments when modal opens - use the appropriate field based on user role
  useEffect(() => {
    if (isOpen) {
      if (userRole === 'admin') {
        // Admin can see and edit admin comments
        setComments(guest.admin_comments || '');
      } else {
        // Host can see and edit host comments
        setComments(guest.host_comments || '');
      }
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    // Cleanup: restore body scroll when modal closes
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, guest.host_comments, guest.admin_comments, userRole]);

  if (!isOpen) return null;

  const guestName = formatGuestEventDisplayName(guest);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updatedGuest = await guestAPI.updateGuestComments(eventId, guest.id, comments);
      
      onUpdate(updatedGuest);
      
      // Auto close after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      logError('Error saving comments', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setComments(guest.host_comments || guest.admin_comments || '');
    onClose();
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Modal */}
      <div 
        className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
            }`}>
              <FileText className={`w-5 h-5 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Comments for {guestName}
              </h3>
              {userRole === 'admin' && guest.admin_comments_updated_at && (
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Last updated {new Date(guest.admin_comments_updated_at).toLocaleString()}
                  {guest.admin_comments_updated_by_name && ` by ${guest.admin_comments_updated_by_name}`}
                </p>
              )}
              {userRole === 'host' && guest.host_comments_updated_at && (
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Last updated {new Date(guest.host_comments_updated_at).toLocaleString()}
                  {guest.host_comments_updated_by_name && ` by ${guest.host_comments_updated_by_name}`}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark' 
                ? 'hover:bg-gray-700' 
                : 'hover:bg-gray-100'
            }`}
          >
            <X className={`w-5 h-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Show read-only view of all comments for admins */}
          {userRole === 'admin' && (guest.host_comments || guest.admin_comments) && (
            <div className={`mb-4 p-4 rounded-lg border ${
              theme === 'dark' 
                ? 'bg-gray-900 border-gray-700' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="space-y-4">
                {guest.host_comments && (
                  <div>
                    <div className={`text-xs font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      HOST NOTES
                      {guest.host_comments_updated_at && (
                        <span className="ml-2 text-xs font-normal">
                          ({new Date(guest.host_comments_updated_at).toLocaleDateString()}
                          {guest.host_comments_updated_by_name && ` by ${guest.host_comments_updated_by_name}`})
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {guest.host_comments}
                    </p>
                  </div>
                )}
                {guest.admin_comments && guest.host_comments && (
                  <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`} />
                )}
                {guest.admin_comments && (
                  <div>
                    <div className={`text-xs font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      ADMIN NOTES
                      {guest.admin_comments_updated_at && (
                        <span className="ml-2 text-xs font-normal">
                          ({new Date(guest.admin_comments_updated_at).toLocaleDateString()}
                          {guest.admin_comments_updated_by_name && ` by ${guest.admin_comments_updated_by_name}`})
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {guest.admin_comments}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Editable notes section */}
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {userRole === 'admin' ? 'Admin Notes' : 'Host Notes'}
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={userRole === 'admin' 
              ? "Add admin notes about this guest..." 
              : "Add notes about this guest (dietary restrictions, special requests, seating preferences, etc.)"
            }
            rows={8}
            className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
              theme === 'dark'
                ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
            } focus:outline-none focus:ring-2 ${
              theme === 'dark' ? 'focus:ring-blue-400' : 'focus:ring-blue-500'
            }`}
          />
          <div className={`flex items-center gap-2 mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <p className="text-xs">These notes are only visible to admins and hosts</p>
            <p className="text-xs">{comments.length} characters</p>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 p-4 border-t ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={handleClose}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Comments
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
